import { Card, Heading } from '@betterment/samba-react';
import { useFeatureFlag } from '@betterment/shared-components';

import { RecurringTransactionsDisclosure } from './RecurringTransactionsDisclosure';

import { PayrollContributionConfigFragment } from '../../../graphql/fragments/payrollContributionConfig.generated';
import { RecurringTransactionConfigFragment } from '../../../graphql/fragments/recurringTransactionConfig.generated';
import { PurposeRecurringTransactionConfigsQuery } from '../../../purpose/components/recurring-transactions-card/graphql/purposeRecurringTransactionConfigs.generated';
import MessageBox from '../message-box/message-box';
import { PayrollContributionConfig } from '../payroll-contribution-config/PayrollContributionConfig';
import {
  RecurringTransactionsConfig,
  configFrequencyCopy,
} from '../recurring-transactions-config/RecurringTransactionsConfig';
import { RecurringTransactionsSetup } from '../recurring-transactions-config/RecurringTransactionsSetup';

type Envelope = PurposeRecurringTransactionConfigsQuery['purpose']['envelope'];
type LegalSubAccount = Extract<Envelope['legalSubAccounts'][number], { __typename: 'CashAccount' }>;
type Disclosure = LegalSubAccount['recurringDepositMotivation'];

type RecurringTransactionsCardViewProps = {
  recurringTransactionConfigs: RecurringTransactionConfigFragment[];
  isWithdrawal: (sourceLegacySubAccountId: number) => boolean;
  payrollContributionConfigs?: PayrollContributionConfigFragment[];
  recurringTransactionGuidance?: Envelope['recurringTransactionMessages'];
  disclosures?: Disclosure;
  subAccountId: string;
};

export const RecurringTransactionsCardView = ({
  recurringTransactionConfigs,
  isWithdrawal,
  recurringTransactionGuidance = [],
  payrollContributionConfigs = [],
  disclosures,
  subAccountId,
}: RecurringTransactionsCardViewProps) => {
  const { enabled: isTransactionCardUpdatesEnabled } = useFeatureFlag({
    splitName: 'retail.px_recurring_transaction_card_q4_2025_enabled',
    context: 'BonusDeferrals',
  });

  const shouldShowRecurringTransactionSetup = !payrollContributionConfigs.length && !recurringTransactionConfigs.length;
  const shouldShowSavingsHeader = !!(disclosures && disclosures.shortDisclosure && disclosures.longDisclosure);
  if (isTransactionCardUpdatesEnabled) {
    return (
      <Card>
        <Card.Header>
          <Heading as="h2" variant="title200" css={{ padding: 'space.300' }}>
            {shouldShowRecurringTransactionSetup ? 'Automate your savings' : 'Recurring transactions'}
          </Heading>
        </Card.Header>
        <Card.Content>{}</Card.Content>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header>
        <Heading as="h2" variant="title200" css={{ padding: 'space.300' }}>
          {shouldShowRecurringTransactionSetup ? 'Automate your savings' : 'Recurring transactions'}
        </Heading>
      </Card.Header>
      <Card.Content>
        {shouldShowRecurringTransactionSetup ? (
          <>
            <RecurringTransactionsSetup subAccountId={subAccountId} subText={disclosures?.description} />
            {shouldShowSavingsHeader && (
              <RecurringTransactionsDisclosure
                shortDisclosure={disclosures.shortDisclosure}
                longDisclosure={disclosures.longDisclosure}
              />
            )}
          </>
        ) : (
          <>
            {payrollContributionConfigs.length > 0 &&
              payrollContributionConfigs.map(config => <PayrollContributionConfig key={config.id} config={config} />)}
            {recurringTransactionConfigs.length > 0 &&
              recurringTransactionConfigs.map(config => (
                <RecurringTransactionsConfig
                  key={config.id}
                  config={config}
                  frequency={configFrequencyCopy(config)}
                  isWithdrawal={isWithdrawal}
                />
              ))}
          </>
        )}
      </Card.Content>
      {!!recurringTransactionGuidance?.length && (
        <Card.Footer
          css={{
            marginX: 'space.300',
            marginBottom: 'space.300',
          }}
        >
          <MessageBox messages={recurringTransactionGuidance} location="envelope_overview_recurring_transactions" />
        </Card.Footer>
      )}
    </Card>
  );
};
